import { useEffect, useState } from 'react'

type Viewport = { height: number; width: number }

function isViewport(data: unknown): data is Viewport & { type: 'hostViewport' } {
  if (typeof data !== 'object' || data === null) return false
  const { type, height, width } = data as Record<string, unknown>
  return type === 'hostViewport' && Number.isFinite(height) && Number.isFinite(width)
}

export function useHostViewportHeight() {
  const framed = window.parent !== window
  const [viewport, setViewport] = useState<Viewport | null>(() =>
    framed ? null : { height: window.innerHeight, width: window.innerWidth }
  )

  useEffect(() => {
    const keep = (next: Viewport) =>
      setViewport((current) => (current && current.width === next.width ? current : next))

    if (!framed) {
      const onResize = () => keep({ height: window.innerHeight, width: window.innerWidth })
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || !isViewport(event.data)) return
      keep({ height: event.data.height, width: event.data.width })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [framed])

  return viewport?.height ?? null
}
